import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Item {
  id: string;
  label: string;
  sub?: string;
}

interface Props {
  label: string;
  items: Item[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  maxHeight?: string;
}

export function MultiEntityPicker({ label, items, selected, onChange, placeholder = 'Buscar...', maxHeight = '160px' }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = search
    ? items.filter(i =>
        i.label.toLowerCase().includes(search.toLowerCase()) ||
        i.sub?.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium leading-none">{label}</span>
        {selected.length > 0 && (
          <span className="text-xs text-muted-foreground">{selected.length} seleccionado{selected.length !== 1 ? 's' : ''}</span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-2">{t('partnerships.multientitypicker.sin_opciones_disponibles_todavia')}</p>
      ) : (
        <div className="border rounded-md overflow-hidden">
          {items.length > 5 && (
            <div className="relative border-b">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={placeholder}
                className="pl-8 border-0 rounded-none h-8 text-xs focus-visible:ring-0"
              />
            </div>
          )}
          <div className="overflow-y-auto" style={{ maxHeight }}>
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground p-2">{t('partnerships.multientitypicker.sin_resultados')}</p>
            ) : (
              filtered.map(item => {
                const isSelected = selected.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggle(item.id)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors border-b last:border-0 ${
                      isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                    }`}
                  >
                    <span className={`h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center text-[10px] ${
                      isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                    }`}>
                      {isSelected && '✓'}
                    </span>
                    <span className="truncate">{item.label}</span>
                    {item.sub && <span className="text-xs text-muted-foreground ml-auto shrink-0">{item.sub}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
